// =============================================================================
// GOUV AUDIT - Widget Gouvernance : Journal d'Audit & Traçabilité
// Migré depuis management/AuditManager.jsx le 22/02/2026
// =============================================================================
import { useState, useEffect } from "react";
import {
  FileText,
  Search,
  Filter,
  Calendar,
  User,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Loader2,
  Clock,
  Shield,
  TrendingUp,
  BarChart3,
  RefreshCw
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs par catégorie d'audit
const CATEGORY_COLORS = {
  system: { bg: "bg-zinc-500/20", text: "text-zinc-400", icon: Shield },
  marketing: { bg: "bg-purple-500/20", text: "text-purple-400", icon: TrendingUp },
  operation: { bg: "bg-orange-500/20", text: "text-orange-400", icon: Activity },
  communication: { bg: "bg-blue-500/20", text: "text-blue-400", icon: Activity },
  expenses: { bg: "bg-amber-500/20", text: "text-amber-400", icon: AlertTriangle },
  security: { bg: "bg-red-500/20", text: "text-red-400", icon: Shield }
};

// Couleurs par action
const ACTION_COLORS = {
  create: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: CheckCircle },
  update: { bg: "bg-blue-500/20", text: "text-blue-400", icon: Activity },
  delete: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle },
  view: { bg: "bg-zinc-500/20", text: "text-zinc-400", icon: Eye },
  login: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: CheckCircle },
  logout: { bg: "bg-zinc-500/20", text: "text-zinc-400", icon: XCircle },
  recommend: { bg: "bg-purple-500/20", text: "text-purple-400", icon: TrendingUp },
  validate: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: CheckCircle },
  reject: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle },
  execute: { bg: "bg-blue-500/20", text: "text-blue-400", icon: Activity }
};

const ACTION_LABELS = {
  create: "Création",
  update: "Modification",
  delete: "Suppression",
  view: "Consultation",
  login: "Connexion",
  logout: "Déconnexion",
  recommend: "Recommandation",
  validate: "Validation",
  reject: "Rejet",
  execute: "Exécution"
};

const CATEGORY_LABELS = {
  system: "Système",
  marketing: "Marketing",
  operation: "Opération",
  communication: "Communication",
  expenses: "Dépenses",
  security: "Sécurité"
};

export default function GouvAudit() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  // 2026-05-12 (JJ) — Section fichiers d'audit (docs/audits/ + docs/superpowers/plans/)
  const [activeTab, setActiveTab] = useState("logs"); // "logs" | "files"
  const [auditFiles, setAuditFiles] = useState([]);
  const [auditCats, setAuditCats] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesFilterCategory, setFilesFilterCategory] = useState("");
  const [filesSearch, setFilesSearch] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [fileContentLoading, setFileContentLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterCategory, filterAction]);

  useEffect(() => {
    if (activeTab === "files") {
      loadAuditFiles();
      loadAuditCategories();
    }
  }, [activeTab, filesFilterCategory, filesSearch]);

  const loadAuditFiles = async () => {
    setFilesLoading(true);
    try {
      let url = `${API_URL}/api/management/audit-files?limit=500`;
      if (filesFilterCategory) url += `&category=${encodeURIComponent(filesFilterCategory)}`;
      if (filesSearch) url += `&search=${encodeURIComponent(filesSearch)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setAuditFiles(data.files || []);
      }
    } catch (err) {
      console.error("Erreur chargement fichiers audit:", err);
    } finally {
      setFilesLoading(false);
    }
  };

  const loadAuditCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/management/audit-files/categories`);
      if (res.ok) {
        const data = await res.json();
        setAuditCats(data.categories || []);
      }
    } catch (err) {
      console.error("Erreur chargement catégories:", err);
    }
  };

  const openAuditFile = async (file) => {
    setSelectedFile(file);
    setFileContent("");
    if (file.ext === "pdf") {
      // PDF : ouverture directe dans nouvel onglet
      window.open(`${API_URL}/api/management/audit-files/${encodeURIComponent(file.name)}`, "_blank");
      setSelectedFile(null);
      return;
    }
    setFileContentLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/management/audit-files/${encodeURIComponent(file.name)}`
      );
      if (res.ok) {
        const data = await res.json();
        setFileContent(data.content || "");
      }
    } catch (err) {
      console.error("Erreur lecture fichier:", err);
    } finally {
      setFileContentLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  };

  const formatFileDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/management/audit?limit=200`;
      if (filterCategory) url += `&category=${filterCategory}`;
      if (filterAction) url += `&action=${filterAction}`;

      const [logsRes, statsRes] = await Promise.all([
        fetch(url),
        fetch(`${API_URL}/api/management/audit/stats?days=7`)
      ]);

      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error loading audit data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.description?.toLowerCase().includes(query) ||
      log.user_name?.toLowerCase().includes(query) ||
      log.user_email?.toLowerCase().includes(query) ||
      log.entity_type?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6" data-testid="audit-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Audit & Traçabilité</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {activeTab === "logs"
              ? "Historique complet de toutes les actions"
              : `Rapports et documents d'audit (${auditFiles.length} fichiers)`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={activeTab === "logs" ? loadData : loadAuditFiles}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="audit-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          {activeTab === "logs" && (
            <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-zinc-800">
        <button
          onClick={() => setActiveTab("logs")}
          className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
            activeTab === "logs"
              ? "text-white border-blue-500"
              : "text-zinc-400 border-transparent hover:text-zinc-200"
          }`}
          data-testid="tab-logs"
        >
          <Activity className="w-4 h-4 inline mr-1" /> Journal d'activité
        </button>
        <button
          onClick={() => setActiveTab("files")}
          className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
            activeTab === "files"
              ? "text-white border-blue-500"
              : "text-zinc-400 border-transparent hover:text-zinc-200"
          }`}
          data-testid="tab-files"
        >
          <FileText className="w-4 h-4 inline mr-1" /> Rapports & Documents
        </button>
      </div>

      {/* Tab Logs */}
      {activeTab === "logs" && (
      <>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_events || 0}</p>
                <p className="text-sm text-zinc-400">Événements (7j)</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.by_action?.validate || 0}</p>
                <p className="text-sm text-zinc-400">Validations</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.by_category?.expenses || 0}</p>
                <p className="text-sm text-zinc-400">Dépenses</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.by_action?.reject || 0}</p>
                <p className="text-sm text-zinc-400">Rejets</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher dans les logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800/50 border-zinc-700"
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 text-white"
        >
          <option value="">Toutes catégories</option>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 text-white"
        >
          <option value="">Toutes actions</option>
          {Object.entries(ACTION_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-1">Aucun événement</h3>
          <p className="text-zinc-400">
            {logs.length === 0 
              ? "Le journal d'audit est vide. Les actions seront enregistrées automatiquement."
              : "Aucun résultat pour ces filtres."}
          </p>
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto oh-responsive-table">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Action</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Catégorie</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Description</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => {
                  const actionColors = ACTION_COLORS[log.action] || ACTION_COLORS.view;
                  const categoryColors = CATEGORY_COLORS[log.category] || CATEGORY_COLORS.system;
                  const ActionIcon = actionColors.icon;

                  return (
                    <tr 
                      key={log.id} 
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${actionColors.bg} ${actionColors.text}`}>
                          <ActionIcon className="w-3 h-3 mr-1" />
                          {ACTION_LABELS[log.action] || log.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${categoryColors.bg} ${categoryColors.text}`}>
                          {CATEGORY_LABELS[log.category] || log.category}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-white truncate max-w-xs" title={log.description}>
                          {log.description || "-"}
                        </p>
                        {log.entity_type && (
                          <p className="text-xs text-zinc-500 mt-0.5">
                            {log.entity_type} {log.entity_id ? `#${log.entity_id.slice(-6)}` : ""}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center">
                            <User className="w-3 h-3 text-zinc-400" />
                          </div>
                          <div>
                            <p className="text-sm text-white">{log.user_name || "Système"}</p>
                            {log.user_email && (
                              <p className="text-xs text-zinc-500">{log.user_email}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                          className="text-zinc-400 hover:text-white"
                          aria-label="Voir le détail de l'événement"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </>
      )}

      {/* Tab Files */}
      {activeTab === "files" && (
        <div className="space-y-4">
          {/* Filtres files */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Rechercher un fichier..."
                value={filesSearch}
                onChange={(e) => setFilesSearch(e.target.value)}
                className="pl-10 bg-zinc-800/50 border-zinc-700 w-full"
                data-testid="audit-files-search"
              />
            </div>
            <select
              value={filesFilterCategory}
              onChange={(e) => setFilesFilterCategory(e.target.value)}
              className="bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 text-white w-full md:w-auto md:max-w-[240px]"
              data-testid="audit-files-category"
            >
              <option value="">Toutes catégories ({auditFiles.length})</option>
              {auditCats.map((c) => (
                <option key={c.label} value={c.label}>
                  {c.label} ({c.count})
                </option>
              ))}
            </select>
          </div>

          {/* Liste files */}
          {filesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : auditFiles.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">Aucun fichier trouvé</p>
            </div>
          ) : (
            <>
              {/* Desktop: table */}
              <div className="hidden md:block bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto oh-responsive-table">
                  <table className="w-full min-w-[760px]">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Nom du fichier</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Catégorie</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Type</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Taille</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400">Modifié</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-zinc-400"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditFiles.map((f) => (
                        <tr
                          key={f.name}
                          className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openAuditFile(f)}
                              className="text-sm text-white hover:text-blue-300 text-left"
                              data-testid={`audit-file-open-${f.name}`}
                            >
                              {f.name}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className="bg-purple-500/20 text-purple-300">{f.category}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs uppercase text-zinc-400">{f.ext}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-zinc-400">{formatFileSize(f.size_bytes)}</td>
                          <td className="px-4 py-3 text-sm text-zinc-400">{formatFileDate(f.mtime)}</td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAuditFile(f)}
                              className="text-zinc-400 hover:text-white"
                              aria-label={`Ouvrir ${f.name}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile: cartes empilées */}
              <div className="md:hidden space-y-3">
                {auditFiles.map((f) => (
                  <button
                    key={f.name}
                    onClick={() => openAuditFile(f)}
                    className="w-full text-left bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-blue-500/50 transition-colors"
                    data-testid={`audit-file-card-${f.name}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm text-white font-medium break-all flex-1">{f.name}</p>
                      <Eye className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge className="bg-purple-500/20 text-purple-300">{f.category}</Badge>
                      <span className="uppercase text-zinc-500">{f.ext}</span>
                      <span className="text-zinc-500">{formatFileSize(f.size_bytes)}</span>
                      <span className="text-zinc-500">•</span>
                      <span className="text-zinc-500">{formatFileDate(f.mtime)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal contenu fichier audit */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-zinc-800">
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-white break-all">
                  {selectedFile.name}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                    {selectedFile.category}
                  </Badge>
                  <span className="text-xs text-zinc-500 uppercase">{selectedFile.ext}</span>
                  <span className="text-xs text-zinc-500">
                    {formatFileSize(selectedFile.size_bytes)}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedFile(null); setFileContent(""); }}
                aria-label="Fermer"
                className="shrink-0"
              >
                ✕
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {fileContentLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              ) : (
                <pre
                  className="text-xs sm:text-sm text-zinc-200 whitespace-pre-wrap break-words font-mono leading-relaxed"
                  data-testid="audit-file-content"
                >
                  {fileContent || "(fichier vide)"}
                </pre>
              )}
            </div>
            <div className="p-4 border-t border-zinc-800 flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = `${API_URL}/api/management/audit-files/${encodeURIComponent(selectedFile.name)}`;
                  a.download = selectedFile.name;
                  a.click();
                }}
                className="border-zinc-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button
                onClick={() => { setSelectedFile(null); setFileContent(""); }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Détail de l'événement</h2>
              <Button variant="ghost" size="sm" onClick={() => setSelectedLog(null)}>
                ✕
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400">Date</label>
                  <p className="text-white">{formatDate(selectedLog.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Action</label>
                  <p className="text-white">{ACTION_LABELS[selectedLog.action] || selectedLog.action}</p>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Catégorie</label>
                  <p className="text-white">{CATEGORY_LABELS[selectedLog.category] || selectedLog.category}</p>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">IP</label>
                  <p className="text-white">{selectedLog.ip_address || "-"}</p>
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-400">Description</label>
                <p className="text-white">{selectedLog.description || "-"}</p>
              </div>

              <div>
                <label className="text-sm text-zinc-400">Utilisateur</label>
                <p className="text-white">{selectedLog.user_name || "Système"}</p>
                {selectedLog.user_email && (
                  <p className="text-sm text-zinc-400">{selectedLog.user_email}</p>
                )}
              </div>

              {selectedLog.entity_type && (
                <div>
                  <label className="text-sm text-zinc-400">Entité</label>
                  <p className="text-white">{selectedLog.entity_type}</p>
                  {selectedLog.entity_id && (
                    <p className="text-sm text-zinc-400">ID: {selectedLog.entity_id}</p>
                  )}
                </div>
              )}

              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <label className="text-sm text-zinc-400">Métadonnées</label>
                  <pre className="bg-zinc-800 rounded-lg p-3 text-sm text-zinc-300 overflow-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="mt-6">
              <Button
                onClick={() => setSelectedLog(null)}
                className="w-full"
                variant="outline"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
